import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Send, Sparkles } from 'lucide-react';
import type { EvaluationExam } from '@interactive-room';
import { EXAM_LEVEL_OPTIONS, type ExamLevelId } from '../constants/examAiParams';
import { resolveExamAiLevel, resolveExamAiSubjectTag } from '../lib/resolveExamAiIdentity';
import { reviseExamFromChat } from './printMaterialChat';

export type PrintChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
};

interface PrintMaterialChatPanelProps {
  exam: EvaluationExam;
  subjectExpertPrompt?: string;
  level?: ExamLevelId | string;
  onExamRevised: (exam: EvaluationExam) => void;
  /** Cuando falta nivel: el docente elige uno y se persiste en el examen. */
  onLevelResolved?: (level: ExamLevelId) => void;
}

function createId() {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

const SUGGESTIONS = [
  'Haz el tono más simple y cercano al alumno',
  'Acorta el bloque 1 y deja un ejemplo concreto',
  'Añade más desafío cognitivo en las preguntas',
];

export function PrintMaterialChatPanel({
  exam,
  subjectExpertPrompt,
  level,
  onExamRevised,
  onLevelResolved,
}: PrintMaterialChatPanelProps) {
  const resolvedLevel = useMemo(() => resolveExamAiLevel(exam, level), [exam, level]);
  const subjectTag = useMemo(() => resolveExamAiSubjectTag(exam), [exam]);
  const needsLevel = !resolvedLevel;

  const [messages, setMessages] = useState<PrintChatMessage[]>(() => [
    {
      id: createId(),
      role: 'assistant',
      content:
        'Pedime cambios sobre la hoja. Podés apuntar a un bloque («bloque 2, más corto») o a toda la guía. La vista de la derecha se actualiza al instante.',
    },
  ]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, progress]);

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;

    if (!resolvedLevel) {
      setMessages((prev) => [
        ...prev,
        { id: createId(), role: 'user', content: text },
        {
          id: createId(),
          role: 'assistant',
          content:
            'Falta el nivel curricular para editar con IA. Elegí el nivel abajo (queda guardado en este material) y volvé a enviar el pedido.',
        },
      ]);
      setDraft('');
      return;
    }

    if (!subjectTag) {
      setMessages((prev) => [
        ...prev,
        { id: createId(), role: 'user', content: text },
        {
          id: createId(),
          role: 'assistant',
          content: 'Este material no tiene asignatura. Volvé al curso y regeneralo o asignale una asignatura.',
        },
      ]);
      setDraft('');
      return;
    }

    setDraft('');
    setMessages((prev) => [...prev, { id: createId(), role: 'user', content: text }]);
    setBusy(true);
    setProgress('Preparando…');

    try {
      const result = await reviseExamFromChat({
        exam,
        message: text,
        subjectExpertPrompt,
        level: resolvedLevel,
        onProgress: (done, total, blockIndex) => {
          setProgress(
            total <= 1
              ? `Reescribiendo bloque #${blockIndex + 1}…`
              : `Bloque #${blockIndex + 1} · ${done}/${total}`,
          );
        },
      });
      onExamRevised(result.exam);
      setMessages((prev) => [
        ...prev,
        { id: createId(), role: 'assistant', content: result.assistantSummary },
      ]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo aplicar el pedido.';
      setMessages((prev) => [...prev, { id: createId(), role: 'assistant', content: msg }]);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <aside className="print-studio-chat no-print" aria-label="Chat de edición con IA">
      <header className="print-studio-chat__head">
        <div className="print-studio-chat__brand">
          <Sparkles className="h-4 w-4" aria-hidden />
          <div>
            <p className="print-studio-chat__eyebrow">Copiloto</p>
            <h2 className="print-studio-chat__title">Editar con IA</h2>
          </div>
        </div>
        <p className="print-studio-chat__hint">
          Pedidos por bloque o sobre toda la guía. La hoja de la derecha se actualiza al instante.
        </p>
        {needsLevel ? (
          <label className="print-studio-chat__level">
            <span>Nivel curricular</span>
            <select
              className="print-studio-chat__level-select"
              value=""
              onChange={(e) => {
                const next = e.target.value as ExamLevelId;
                if (next) onLevelResolved?.(next);
              }}
            >
              <option value="">Elegir nivel…</option>
              {EXAM_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="print-studio-chat__meta">
            Nivel: {EXAM_LEVEL_OPTIONS.find((o) => o.id === resolvedLevel)?.label ?? resolvedLevel}
          </p>
        )}
      </header>

      <div className="print-studio-chat__messages" ref={listRef}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`print-studio-chat__bubble print-studio-chat__bubble--${msg.role}`}
          >
            {msg.content}
          </div>
        ))}
        {progress ? (
          <div className="print-studio-chat__bubble print-studio-chat__bubble--system" role="status">
            <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" aria-hidden />
            {progress}
          </div>
        ) : null}
      </div>

      {!busy ? (
        <div className="print-studio-chat__suggestions">
          {SUGGESTIONS.map((chip) => (
            <button
              key={chip}
              type="button"
              className="print-studio-chat__chip"
              onClick={() => void send(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      ) : null}

      <form
        className="print-studio-chat__composer"
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
      >
        <textarea
          className="print-studio-chat__input"
          rows={3}
          value={draft}
          disabled={busy}
          placeholder="Ej: en el bloque 2, usa números más chicos…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(draft);
            }
          }}
        />
        <button type="submit" className="print-studio-chat__send" disabled={busy || !draft.trim()}>
          <Send className="h-4 w-4" aria-hidden />
          Enviar
        </button>
      </form>
    </aside>
  );
}
